// Kinio 攝影機通道（SPEC #kinio-station-kinio-cam，Eddy 2026-10-16 選單）：Kinio Station 的一路拉一台 Kinio 攝影機。
//   - Station：我能管理（owner / admin）的錄影主機
//   - 攝影機：我有即時影像（media.live）的攝影機與錄影主機通道——不限同家戶，別的家戶、別人分享給我的都列（GET /v1/me/devices）
//   - 平台記「誰設的」：Station 之後拉流的權限算設定者的；設定者失去權限（分享撤銷、移出組織…）通道就停，清單寫原因
//   - 本機網頁設不了（nvrd 回 KINIO_SOURCE_CLOUD_ONLY），只能從 App / VMS 設
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useDevices } from '../../lib/useDevices';
import { useScope } from '../../lib/scope';
import { kinioChannelApi } from '../../lib/planeApi';
import { CHILD_SEP } from '../../lib/devices';
import { canSetChannels } from './gatewayAccess';
import { aidKind } from '../../lib/pairName';
import { gatewaysOf } from './OnvifDialog';
import type { MyDevice } from '../../lib/myDevices';

export interface Candidate {
  ref: string;
  label: string;
}

/**
 * 能接到這台 Station 的攝影機：同一個 plane、我有 media.live 的攝影機本體（CAM / 門鈴）與錄影主機通道；不含這台 Station 自己與它的通道。
 * 來源是 GET /v1/me/devices（不限目前範圍：別的家戶、別人分享給我的都在）。
 */
export function kinioCandidates(devices: MyDevice[], gw: { aid: string; apiEndpoint: string }): Candidate[] {
  const out: Candidate[] = [];
  const live = (acts: ({ action: string } | string)[]) => acts.some((a) => (typeof a === 'string' ? a : a.action) === 'media.live');
  for (const d of devices) {
    if (d.api !== gw.apiEndpoint || d.device_ref === gw.aid) continue;
    const kind = aidKind(d.device_ref);
    if ((kind === 'camera' || kind === 'doorbell') && live(d.actions)) out.push({ ref: d.device_ref, label: d.display_name || d.device_ref });
    for (const c of d.children ?? []) {
      if (live(c.actions)) out.push({ ref: c.device_ref, label: `${d.display_name || d.device_ref} · ${c.display_name || 'CH' + (Number(c.device_ref.split(CHILD_SEP)[1]) + 1)}` });
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** 不成立原因 → 文案 key（沒對照的退回 generic）。 */
const DENIED = ['not_granted', 'org_mfa_required', 'ip_not_allowed', 'session_reauth', 'share_expired', 'share_suspended', 'alarm_window', 'armed', 'schedule', 'org_suspended', 'station_rebound', 'device_unbound', 'no_assignment'];

export function KinioChannelDialog({ open, close }: { open: boolean; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { tree } = useDevices();
  const { devices, homes, orgs } = useScope();
  // Station 排前面（KST）；其他錄影主機若機種沒開這個選項，平台回 KINIO_SOURCE_UNSUPPORTED
  const stations = useMemo(
    () => gatewaysOf(tree).filter((d) => canSetChannels(d, homes, orgs) && !d.seatMode).sort((a, b) => Number(!a.aid.startsWith('KST-')) - Number(!b.aid.startsWith('KST-'))),
    [tree, homes, orgs],
  );
  const [gwKey, setGwKey] = useState('');
  const gw = stations.find((g) => g.key === gwKey) ?? stations[0];
  const [ref, setRef] = useState('');
  const [channel, setChannel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const candidates = useMemo(() => (gw ? kinioCandidates(devices, gw) : []), [devices, gw]);

  const list = useQuery({
    queryKey: ['kinio-channels', gw?.homeId, gw?.aid],
    queryFn: () => kinioChannelApi.list(gw!.apiEndpoint, gw!.homeId, gw!.aid),
    enabled: open && !!gw,
    retry: false,
  });
  const labelOf = (r: string) => candidates.find((c) => c.ref === r)?.label ?? r;
  const errText = (e: unknown) => {
    if (e instanceof KinioError) {
      const k = `kinioch.err_${e.code}`;
      const msg = t(k);
      return msg === k ? `${e.code}${e.message ? ' · ' + e.message : ''}` : msg;
    }
    return String(e);
  };

  async function add() {
    if (!gw || !ref) return;
    setBusy(true);
    setErr(null);
    setDone(null);
    try {
      const ch = channel === '' ? undefined : Number(channel) - 1;
      const r = await kinioChannelApi.set(gw.apiEndpoint, gw.homeId, { gateway: gw.aid, device_ref: ref, channel: ch, name: labelOf(ref).slice(0, 64) });
      setDone(t('kinioch.added', { ch: r.channel + 1, name: labelOf(r.device_ref) }));
      setRef('');
      await Promise.all([list.refetch(), qc.invalidateQueries({ queryKey: ['home'] })]);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(ch: number) {
    if (!gw) return;
    setBusy(true);
    setErr(null);
    setDone(null); // 「已接到 CHn」不留在移除之後
    try {
      await kinioChannelApi.remove(gw.apiEndpoint, gw.homeId, gw.aid, ch);
      await Promise.all([list.refetch(), qc.invalidateQueries({ queryKey: ['home'] })]);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title={t('kinioch.title')} onClose={close} wide>
      <div className="form">
        <p className="muted small">{t('kinioch.help')}</p>
        {!stations.length ? (
          <p className="notice">{t('kinioch.no_station')}</p>
        ) : (
          <>
            <label className="field">
              <span>{t('kinioch.station')}</span>
              <select className="input" value={gw?.key ?? ''} onChange={(e) => setGwKey(e.target.value)} data-testid="kinioch-station">
                {stations.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.name} · {g.homeName} {g.presence === 'online' ? '' : t('common.paren', { x: t('devices.offline') })}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('kinioch.camera')}</span>
              {candidates.length ? (
                <select className="input" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="kinioch-camera">
                  <option value="">{t('kinioch.pick_camera')}</option>
                  {candidates.map((c) => (
                    <option key={c.ref} value={c.ref}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="muted small">{t('kinioch.no_camera')}</span>
              )}
            </label>
            <label className="field">
              <span>{t('kinioch.channel')}</span>
              <input className="input" inputMode="numeric" value={channel} onChange={(e) => setChannel(e.target.value.replace(/\D/g, ''))} placeholder={t('kinioch.channel_auto')} data-testid="kinioch-channel" />
            </label>
            <p className="muted small">{t('kinioch.whose')}</p>
            <div className="toolbar">
              <button className="btn btn-primary" onClick={() => void add()} disabled={busy || !ref || gw?.presence !== 'online'} data-testid="kinioch-add">
                {t('kinioch.add')}
              </button>
            </div>
            {done && <p className="notice">{done}</p>}
            {err && <p className="error">{err}</p>}
            <fieldset className="field">
              <legend>{t('kinioch.current')}</legend>
              {list.isError ? (
                <p className="error small">{errText(list.error)}</p>
              ) : !list.data?.data.length ? (
                <p className="muted small">{t('kinioch.none')}</p>
              ) : (
                <ul className="onvif-list" data-testid="kinioch-list">
                  {list.data.data.map((c) => (
                    <li key={c.channel} className="onvif-row">
                      <span className="mono">CH{c.channel + 1}</span>
                      <span>{labelOf(c.device_ref)}</span>
                      <span className="muted small">{c.mine ? t('kinioch.by_me') : t('kinioch.by_other')}</span>
                      {c.authorized ? (
                        <span className="badge onvif-ok">{t('kinioch.ok')}</span>
                      ) : (
                        <span className="badge onvif-auth_failed" title={c.denied}>
                          {t(DENIED.includes(c.denied ?? '') ? `kinioch.denied_${c.denied}` : 'kinioch.denied')}
                        </span>
                      )}
                      <button className="btn btn-sm" onClick={() => void remove(c.channel)} disabled={busy}>
                        {t('kinioch.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          </>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={close} data-testid="kinioch-cancel">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
